'use client'

import React, { useState, useEffect } from 'react'
import { Header } from '@/payload-types'
import { Link } from '@/i18n/routing'
import { XIcon } from 'lucide-react'
import { cn } from '@/utilities/cn'

interface AnnouncementBannerProps {
    announcement: Header['announcement']
}

// Simple hash function to create a unique ID for each announcement message
function hashString(str: string): string {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
}

export function AnnouncementBanner({ announcement }: AnnouncementBannerProps) {
    const [isDismissed, setIsDismissed] = useState(false)
    const [isMounted, setIsMounted] = useState(false)

    // Create a unique storage key based on the announcement text
    // This way, when the message changes, the banner shows again
    const messageHash = announcement?.text ? hashString(announcement.text) : ''
    const storageKey = `announcement-dismissed-${messageHash}`

    useEffect(() => {
        setIsMounted(true)
        // Check if THIS specific announcement was previously dismissed
        const dismissed = localStorage.getItem(storageKey)
        if (dismissed === 'true') {
            setIsDismissed(true)
        }
    }, [storageKey])

    if (!announcement?.enabled || !announcement?.text || isDismissed) {
        return null
    }

    const handleDismiss = () => {
        localStorage.setItem(storageKey, 'true')
        setIsDismissed(true)
    }

    const variant = announcement.variant || 'info'

    const variantStyles = {
        info: 'bg-blue-500/10 border-blue-500/30 text-blue-100',
        warning: 'bg-orange-500/10 border-orange-500/30 text-orange-100',
        error: 'bg-red-500/10 border-red-500/30 text-red-100',
    }

    const buttonStyles = {
        info: 'bg-blue-500/20 hover:bg-blue-500/30 border-blue-500/40 text-blue-100',
        warning: 'bg-orange-500/20 hover:bg-orange-500/30 border-orange-500/40 text-orange-100',
        error: 'bg-red-500/20 hover:bg-red-500/30 border-red-500/40 text-red-100',
    }

    return (
        <div
            className={cn(
                'w-full min-h-12 border-b backdrop-blur-xl shadow-lg flex items-center justify-center px-4 py-2 relative animate-in slide-in-from-top duration-300',
                variantStyles[variant],
            )}
        >
            <div className="container flex items-start justify-between gap-3 sm:gap-4">
                <div className="flex flex-col items-start gap-3 flex-1 min-w-0 sm:flex-row sm:items-center sm:gap-4">
                    <p className="text-sm leading-snug text-left font-medium flex-1 min-w-0 whitespace-normal break-words sm:text-base sm:leading-normal sm:text-center">
                        {announcement.text}
                    </p>

                    {announcement.link?.url && announcement.link?.text && (
                        <Link
                            href={announcement.link.url}
                            className={cn(
                                'px-4 py-1.5 rounded-md border text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0',
                                buttonStyles[variant],
                            )}
                        >
                            {announcement.link.text}
                        </Link>
                    )}
                </div>

                <button
                    onClick={handleDismiss}
                    className="mt-0.5 p-1.5 hover:bg-white/10 rounded-full transition-all duration-200 flex-shrink-0"
                    aria-label="Dismiss announcement"
                >
                    <XIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    )
}
